import React from 'react';

type Props = {
  customerResourceModal: any | null;
  activeModalCustomer: any | null;
  activeModalCustomerDetailReady: boolean;
  activeModalCustomerDetailLoading: boolean;
  activeModalCustomerDetailError: string | null;
  styles: any;
  closeCustomerResourceModal: () => void;
  modalCases: any[];
  modalDevices: any[];
  modalContacts: any[];
  modalLocationTree: any[];
  modalLocationMap: Map<string, any>;
  getCaseDisplayNumber: (caseItem: any) => string;
  locationDisplayPath: (locationMap: Map<string, any>, locationId?: string | null) => string | null;
  formatLocationAddress: (location?: any) => string;
  flattenLocationOptions: (nodes: any[], depth?: number) => { value: string; label: string }[];
  buildLocationTree: (locations?: any[]) => any[];
  setSelectedCase: React.Dispatch<React.SetStateAction<any>>;
  setActiveTab: React.Dispatch<React.SetStateAction<any>>;
  setCustomerFilter: React.Dispatch<React.SetStateAction<string>>;
  canManageCustomers: boolean;
  openContactForm: (customerId: string, contact?: any, locationId?: string) => void;
  contactFormCustomerId: string | null;
  contactForm: any;
  setContactForm: React.Dispatch<React.SetStateAction<any>>;
  handleSubmitContact: () => void | Promise<void>;
  savingContact: boolean;
  resetContactForm: () => void;
  portalContactActionKey: string | null;
  handleToggleContactStatus: (customerId: string, contact: any) => void | Promise<void>;
  handleInvitePortalUser: (customerId: string, contact: any, options?: any) => void | Promise<void>;
  handleSetPortalUserActive: (customerId: string, contact: any, isActive: boolean) => void | Promise<void>;
  handleSetPrimaryContact: (customerId: string, contact: any) => void | Promise<void>;
  openContactFormForAdditionalLocation: (customerId: string, contact: any) => void;
  openLocationForm: (customerId: string, location?: any, parentLocationId?: string) => void;
  locationFormCustomerId: string | null;
  locationForm: any;
  setLocationForm: React.Dispatch<React.SetStateAction<any>>;
  handleSubmitLocation: () => boolean | void | Promise<boolean | void>;
  savingLocation: boolean;
  resetLocationForm: () => void;
  handleToggleLocationStatus: (customerId: string, location: any) => void | Promise<void>;
  handleSetPrimaryLocation: (customerId: string, location: any) => void | Promise<void>;
  setCustomerResourceModal: React.Dispatch<React.SetStateAction<any>>;
};

export const TechDashboardCustomerResourceModal = React.memo(function TechDashboardCustomerResourceModal({
  customerResourceModal,
  activeModalCustomer,
  activeModalCustomerDetailReady,
  activeModalCustomerDetailLoading,
  activeModalCustomerDetailError,
  styles,
  closeCustomerResourceModal,
  modalCases,
  modalDevices,
  modalContacts,
  modalLocationTree,
  modalLocationMap,
  getCaseDisplayNumber,
  locationDisplayPath,
  formatLocationAddress,
  flattenLocationOptions,
  buildLocationTree,
  setSelectedCase,
  setActiveTab,
  setCustomerFilter,
  canManageCustomers,
  openContactForm,
  contactFormCustomerId,
  contactForm,
  setContactForm,
  handleSubmitContact,
  savingContact,
  resetContactForm,
  portalContactActionKey,
  handleToggleContactStatus,
  handleInvitePortalUser,
  handleSetPortalUserActive,
  handleSetPrimaryContact,
  openContactFormForAdditionalLocation,
  openLocationForm,
  locationFormCustomerId,
  locationForm,
  setLocationForm,
  handleSubmitLocation,
  savingLocation,
  resetLocationForm,
  handleToggleLocationStatus,
  handleSetPrimaryLocation,
  setCustomerResourceModal
}: Props) {
  if (!customerResourceModal || !activeModalCustomer) return null;
  const detailRequired = customerResourceModal.mode !== 'cases';
  const showDetailLoading =
    detailRequired &&
    (!activeModalCustomerDetailReady || activeModalCustomerDetailLoading);
  const availableParentLocations = flattenLocationOptions(modalLocationTree).filter(
    option => option.value !== locationForm.id
  );
  const defaultParentLocationId = String(
    (
      (activeModalCustomer.locations ?? []).find(
        (location: any) =>
          location?.isPrimary &&
          String(location?.id || '').trim() !== String(locationForm.id || '').trim()
      ) ||
      (activeModalCustomer.locations ?? []).find(
        (location: any) => String(location?.id || '').trim() !== String(locationForm.id || '').trim()
      ) ||
      null
    )?.id || ''
  ).trim();

  return (
    <div style={styles.modal} onClick={closeCustomerResourceModal}>
      <div style={styles.modalContentWide} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            {(activeModalCustomer.companyName ?? (activeModalCustomer as any).company ?? 'Customer')}{' '}
            {customerResourceModal.mode === 'cases'
              ? 'Cases'
              : customerResourceModal.mode === 'devices'
              ? 'Devices'
              : customerResourceModal.mode === 'contacts'
              ? 'Contacts'
              : 'Locations'}
          </h2>
          <button style={styles.closeButton} onClick={closeCustomerResourceModal}>X</button>
        </div>

        {showDetailLoading ? (
          <div style={styles.modalSection}>
            <div style={styles.emptySubtle}>
              {activeModalCustomerDetailError || 'Loading customer details...'}
            </div>
          </div>
        ) : customerResourceModal.mode === 'cases' ? (
          <>
            <div style={styles.modalSection}>
              {modalCases.length === 0 ? (
                <div style={styles.emptySubtle}>No cases for this customer yet.</div>
              ) : (
                <div style={styles.modalList}>
                  {modalCases.map(caseItem => {
                    const caseNumber = getCaseDisplayNumber(caseItem);
                    const createdLabel = caseItem.createdAt
                      ? new Date(caseItem.createdAt).toLocaleDateString()
                      : 'Unknown date';
                    const problemSnippet = ((caseItem as any).problemDescription ?? (caseItem as any).problemDesc ?? '')
                      .slice(0, 140);
                    return (
                      <div key={caseItem.id} style={styles.modalListItem}>
                        <div style={styles.modalItemTitle}>Case #{caseNumber}</div>
                        <div style={styles.modalItemMeta}>
                          Status: {caseItem.status} | Priority: {caseItem.priority ?? 'Unspecified'}
                        </div>
                        {caseItem.device && (
                          <div style={styles.modalItemMeta}>
                            Device: {caseItem.device.modelNumber ?? 'Unknown model'}
                            {caseItem.device.serialNumber ? ` | ${caseItem.device.serialNumber}` : ''}
                          </div>
                        )}
                        <div style={styles.modalItemMeta}>Created: {createdLabel}</div>
                        {problemSnippet && (
                          <div style={{ ...styles.modalItemMeta, color: '#1f2937' }}>
                            {problemSnippet}
                            {problemSnippet.length === 140 ? '...' : ''}
                          </div>
                        )}
                        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                          <button
                            style={{ ...styles.button, ...styles.secondaryButton }}
                            onClick={() => {
                              setSelectedCase(caseItem);
                              closeCustomerResourceModal();
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                Total cases: {modalCases.length}
              </span>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => {
                  setActiveTab('cases');
                  setCustomerFilter(activeModalCustomer.id || '__unassigned__');
                  closeCustomerResourceModal();
                }}
              >
                Open in Cases tab
              </button>
            </div>
          </>
        ) : customerResourceModal.mode === 'devices' ? (
          <>
            <div style={styles.modalSection}>
              {modalDevices.length === 0 ? (
                <div style={styles.emptySubtle}>No devices assigned to this customer yet.</div>
              ) : (
                <div style={styles.modalList}>
                  {modalDevices.map(device => {
                    const lifecycle = (device.lifecycleStatus ?? (device.isActive ? 'DEPLOYED' : 'INACTIVE')) as string;
                    const statusLabel = (() => {
                      switch (lifecycle) {
                        case 'DEPLOYED':
                          return 'Active';
                        case 'INVENTORY':
                          return 'Inventory';
                        case 'RETIRED':
                          return 'Retired';
                        case 'INACTIVE':
                          return 'Inactive';
                        default:
                          return lifecycle || 'Unknown';
                      }
                    })();
                    const locationLabel =
                      locationDisplayPath(modalLocationMap, device.locationId) ?? 'Unassigned';
                    return (
                      <div key={device.id} style={styles.modalListItem}>
                        <div style={styles.modalItemTitle}>{device.modelNumber ?? 'Unknown model'}</div>
                        <div style={styles.modalItemMeta}>Serial: {device.serialNumber || 'Unspecified'}</div>
                        <div style={styles.modalItemMeta}>Status: {statusLabel}</div>
                        <div style={styles.modalItemMeta}>Location: {locationLabel}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                Total devices: {modalDevices.length}
              </span>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => {
                  setActiveTab('models');
                  closeCustomerResourceModal();
                }}
              >
                Open Devices tab
              </button>
            </div>
          </>
        ) : customerResourceModal.mode === 'contacts' ? (
          <>
            <div style={styles.modalSection}>
              <div style={styles.emptySubtle}>
                Tip: Portal access is linked to one contact record, but that portal user can still access the whole customer account across locations.
              </div>
              {canManageCustomers && (
                <button
                  style={{ ...styles.button, ...styles.secondaryButton, width: 'fit-content' }}
                  onClick={() => openContactForm(activeModalCustomer.id)}
                >
                  + Add Contact
                </button>
              )}
              {canManageCustomers && contactFormCustomerId === activeModalCustomer.id && (
                <div style={styles.inlineForm}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>
                      {contactForm.id ? 'Edit Contact' : 'Add Contact'}
                    </h3>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Full Name *</label>
                      <input
                        style={styles.inputField}
                        value={contactForm.fullName}
                        onChange={e =>
                          setContactForm((prev: any) => ({ ...prev, fullName: e.target.value }))
                        }
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Role / Title</label>
                      <input
                        style={styles.inputField}
                        value={contactForm.role}
                        onChange={e =>
                          setContactForm((prev: any) => ({ ...prev, role: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Email</label>
                      <input
                        style={styles.inputField}
                        value={contactForm.email}
                        onChange={e =>
                          setContactForm((prev: any) => ({ ...prev, email: e.target.value }))
                        }
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Phone</label>
                      <input
                        style={styles.inputField}
                        value={contactForm.phone}
                        onChange={e =>
                          setContactForm((prev: any) => ({ ...prev, phone: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Location</label>
                      <select
                        style={styles.inputField}
                        value={contactForm.locationId}
                        onChange={e =>
                          setContactForm((prev: any) => ({ ...prev, locationId: e.target.value }))
                        }
                      >
                        <option value="">Company level</option>
                        {flattenLocationOptions(buildLocationTree(activeModalCustomer.locations ?? [])).map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={contactForm.isPrimary}
                      onChange={e =>
                        setContactForm((prev: any) => ({ ...prev, isPrimary: e.target.checked }))
                      }
                    />
                    Primary contact for this {contactForm.locationId ? 'location' : 'customer'}
                  </label>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={contactForm.portalAccessMode === 'LITE'}
                      onChange={e =>
                        setContactForm((prev: any) => ({
                          ...prev,
                          portalAccessMode: e.target.checked ? 'LITE' : 'FULL'
                        }))
                      }
                    />
                    Lite portal access
                  </label>
                  {contactForm.id && (
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={contactForm.isActive}
                        onChange={e =>
                          setContactForm((prev: any) => ({ ...prev, isActive: e.target.checked }))
                        }
                      />
                      Active
                    </label>
                  )}
                  <div style={styles.actionRow}>
                    <button
                      style={{ ...styles.button, ...styles.primaryButton }}
                      onClick={() => void handleSubmitContact()}
                      disabled={savingContact}
                    >
                      {savingContact
                        ? 'Saving...'
                        : contactForm.id
                        ? 'Update Contact'
                        : 'Add Contact'}
                    </button>
                    <button
                      style={{ ...styles.button, ...styles.secondaryButton }}
                      onClick={resetContactForm}
                      disabled={savingContact}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {modalContacts.length === 0 ? (
                <div style={styles.emptySubtle}>No contacts for this customer yet.</div>
              ) : (
                <div style={styles.modalList}>
                  {modalContacts.map(contact => {
                    const portalUser = contact.portalUser || null;
                    const portalActionBusy =
                      portalContactActionKey === `invite:${contact.id}` ||
                      portalContactActionKey === `status:${contact.id}`;
                    const locationLabel = locationDisplayPath(modalLocationMap, contact.locationId) || 'Company level';
                    return (
                      <div key={contact.id} style={styles.modalListItem}>
                        <div style={styles.modalItemTitle}>
                          {contact.fullName}
                          {contact.isPrimary ? ' (Primary)' : ''}
                        </div>
                        <div style={styles.modalItemMeta}>Role: {contact.role || 'Not set'}</div>
                        <div style={styles.modalItemMeta}>Email: {contact.email || 'Not set'}</div>
                        <div style={styles.modalItemMeta}>Phone: {contact.phone || 'Not set'}</div>
                        <div style={styles.modalItemMeta}>Location: {locationLabel}</div>
                        <div style={{ ...styles.modalItemMeta, color: contact.isActive ? '#166534' : '#991b1b' }}>
                          {contact.isActive ? 'Active' : 'Inactive'}{contact.email ? ` | ${portalUser ? (portalUser.isActive ? 'Portal enabled' : 'Portal disabled') : 'Portal off'}` : ''}
                          {(contact.portalAccessMode === 'LITE' || portalUser?.portalAccessMode === 'LITE') ? ' | Lite portal' : ''}
                        </div>
                        {canManageCustomers && (
                          <div style={styles.contactActions}>
                            <button
                              style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                              onClick={() => openContactForm(activeModalCustomer.id, contact)}
                            >
                              Edit
                            </button>
                            <button
                              style={{
                                ...styles.actionPill,
                                ...(contact.isActive ? styles.actionPillDanger : styles.actionPillSuccess)
                              }}
                              onClick={() => void handleToggleContactStatus(activeModalCustomer.id, contact)}
                            >
                              {contact.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            {contact.email && (
                              <>
                                <button
                                  style={{ ...styles.actionPill, ...styles.actionPillPrimary }}
                                  onClick={() => void handleInvitePortalUser(activeModalCustomer.id, contact, { resetCredentials: true })}
                                  disabled={portalActionBusy}
                                >
                                  {portalUser ? 'Resend Invite' : 'Enable Portal'}
                                </button>
                                {portalUser && (
                                  <button
                                    style={{
                                      ...styles.actionPill,
                                      ...(portalUser.isActive ? styles.actionPillWarning : styles.actionPillSuccess)
                                    }}
                                    onClick={() => void handleSetPortalUserActive(activeModalCustomer.id, contact, !portalUser.isActive)}
                                    disabled={portalActionBusy}
                                  >
                                    {portalUser.isActive ? 'Disable Portal' : 'Enable Portal'}
                                  </button>
                                )}
                              </>
                            )}
                            {!contact.isPrimary && contact.isActive && (
                              <button
                                style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                                onClick={() => void handleSetPrimaryContact(activeModalCustomer.id, contact)}
                              >
                                Mark Primary
                              </button>
                            )}
                            {contact.isActive && (
                              <button
                                style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                                onClick={() => openContactFormForAdditionalLocation(activeModalCustomer.id, contact)}
                              >
                                + Add location link
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                Total contacts: {modalContacts.length}
              </span>
            </div>
          </>
        ) : (
          <>
            <div style={styles.modalSection}>
              {canManageCustomers && (
                <button
                  style={{ ...styles.button, ...styles.secondaryButton, width: 'fit-content' }}
                  onClick={() => openLocationForm(activeModalCustomer.id)}
                >
                  + Add Location
                </button>
              )}
              {canManageCustomers && locationFormCustomerId === activeModalCustomer.id && (
                <div style={styles.inlineForm}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>
                      {locationForm.id ? 'Edit Location' : 'Add Location'}
                    </h3>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Location Name *</label>
                      <input
                        style={styles.inputField}
                        value={locationForm.name}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, name: e.target.value }))
                        }
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Address line 1</label>
                      <input
                        style={styles.inputField}
                        value={locationForm.addressLine1}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, addressLine1: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Address line 2</label>
                      <input
                        style={styles.inputField}
                        value={locationForm.addressLine2}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, addressLine2: e.target.value }))
                        }
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>City / Town</label>
                      <input
                        style={styles.inputField}
                        value={locationForm.city}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, city: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>State / Province</label>
                      <input
                        style={styles.inputField}
                        value={locationForm.stateProvince}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, stateProvince: e.target.value }))
                        }
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Postal code</label>
                      <input
                        style={styles.inputField}
                        value={locationForm.postalCode}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, postalCode: e.target.value }))
                        }
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Country</label>
                      <input
                        style={styles.inputField}
                        value={locationForm.country}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, country: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Sub-location of</label>
                      <select
                        style={styles.inputField}
                        value={locationForm.parentLocationId}
                        disabled={Boolean(locationForm.isPrimary)}
                        onChange={e =>
                          setLocationForm((prev: any) => ({ ...prev, parentLocationId: e.target.value }))
                        }
                      >
                        {locationForm.isPrimary ? (
                          <option value="">Primary location</option>
                        ) : availableParentLocations.length === 0 ? (
                          <option value="">No parent location available yet</option>
                        ) : (
                          availableParentLocations.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))
                        )}
                      </select>
                      {!locationForm.isPrimary && defaultParentLocationId ? (
                        <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.35rem' }}>
                          Select a higher location for this sub-location. Default is Primary.
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={Boolean(locationForm.isPrimary)}
                      onChange={e =>
                        setLocationForm((prev: any) => ({
                          ...prev,
                          isPrimary: e.target.checked,
                          parentLocationId: e.target.checked
                            ? ''
                            : prev.parentLocationId || defaultParentLocationId || ''
                        }))
                      }
                    />
                    Primary location
                  </label>
                  <div style={styles.actionRow}>
                    <button
                      style={{ ...styles.button, ...styles.primaryButton }}
                      onClick={() => void handleSubmitLocation()}
                      disabled={savingLocation}
                    >
                      {savingLocation
                        ? 'Saving...'
                        : locationForm.id
                        ? 'Update Location'
                        : 'Add Location'}
                    </button>
                    <button
                      style={{ ...styles.button, ...styles.secondaryButton }}
                      onClick={resetLocationForm}
                      disabled={savingLocation}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {modalLocationTree.length === 0 ? (
                <div style={styles.emptySubtle}>No locations for this customer yet.</div>
              ) : (
                <div style={styles.modalList}>
                  {modalLocationTree.map(node => {
                    const renderTree = (currentNode: any, depth = 0): React.ReactNode => {
                      const contactsForLocation = modalContacts.filter(contact => contact.locationId === currentNode.id);
                      const devicesForLocation = modalDevices.filter(device => device.locationId === currentNode.id);
                      const indentation = depth > 0 ? { marginLeft: `${depth * 1.25}rem` } : null;
                      return (
                        <React.Fragment key={`${currentNode.id}-${depth}`}>
                          <div style={{ ...styles.modalListItem, ...(indentation || {}) }}>
                            <div style={styles.modalItemTitle}>
                              {currentNode.name}
                              {currentNode.isPrimary ? ' (Primary)' : ''}
                            </div>
                            <div style={styles.modalItemMeta}>
                              {formatLocationAddress(currentNode) || 'No mailing address set'}
                            </div>
                            <div style={styles.modalItemMeta}>
                              City: {currentNode.city || 'Not set'} | Contacts: {contactsForLocation.length} | Devices: {devicesForLocation.length}
                            </div>
                            {canManageCustomers && (
                              <div style={styles.contactActions}>
                                <button
                                  style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                                  onClick={() => openLocationForm(activeModalCustomer.id, currentNode)}
                                >
                                  Edit
                                </button>
                                {!currentNode.isPrimary && currentNode.isActive && (
                                  <button
                                    style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                                    onClick={() =>
                                      void handleSetPrimaryLocation(activeModalCustomer.id, currentNode)
                                    }
                                  >
                                    Make Primary
                                  </button>
                                )}
                                <button
                                  style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                                  onClick={() => openLocationForm(activeModalCustomer.id, null, currentNode.id)}
                                >
                                  + Sub-location
                                </button>
                                <button
                                  style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                                  onClick={() => {
                                    setCustomerResourceModal({ customerId: activeModalCustomer.id, mode: 'contacts' });
                                    openContactForm(activeModalCustomer.id, null, currentNode.id);
                                  }}
                                >
                                  + Contact
                                </button>
                                <button
                                  style={{ ...styles.actionPill, ...(currentNode.isActive ? styles.actionPillDanger : styles.actionPillSuccess) }}
                                  onClick={() => void handleToggleLocationStatus(activeModalCustomer.id, currentNode)}
                                >
                                  {currentNode.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                              </div>
                            )}
                          </div>
                          {(currentNode.children ?? []).map((child: any) => renderTree(child, depth + 1))}
                        </React.Fragment>
                      );
                    };
                    return renderTree(node, 0);
                  })}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                Total locations: {activeModalCustomer.locations?.length ?? 0}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

